import { Stack } from 'expo-router'
import Head from 'expo-router/head'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Linking, Pressable, ScrollView, View } from 'react-native'
import Footer from '@/components/Footer'
import PublicHeader from '@/components/PublicHeader'
import { Text } from '@/components/ui/Text'
import { useThemeColors } from '@/contexts/ThemeContext'
import { serializeJsonForScriptTag } from '@/lib/jsonForScriptTag'

export default function FAQ() {
  const { t } = useTranslation()
  const colors = useThemeColors()
  const [scrollOffset, setScrollOffset] = useState(0)

  const sections = [
    { title: `1. ${t('faq.section1Question')}`, body: t('faq.section1Answer') },
    { title: `2. ${t('faq.section2Question')}`, body: t('faq.section2Answer') },
    { title: `3. ${t('faq.section3Question')}`, body: t('faq.section3Answer') },
    { title: `4. ${t('faq.section4Question')}`, body: t('faq.section4Answer') },
    { title: `5. ${t('faq.section5Question')}`, body: t('faq.section5Answer') },
    { title: `6. ${t('faq.section6Question')}`, body: t('faq.section6Answer') },
    { title: `7. ${t('faq.section7Question')}`, body: t('faq.section7Answer') },
    { title: `8. ${t('faq.section8Question')}`, body: t('faq.section8Answer') },
    { title: `9. ${t('faq.section9Question')}`, body: t('faq.section9Answer') },
    { title: `10. ${t('faq.section10Question')}`, body: t('faq.section10Answer') },
  ]

  return (
    <View className="flex-1" style={{ backgroundColor: colors.background }}>
      <Head>
        <title>{t('faq.pageTitle')}</title>
        <meta name="description" content={t('faq.pageDescription')} />
        <link rel="canonical" href="https://turbinago.com/faq" />
        <meta property="og:title" content={t('faq.pageTitle')} />
        <meta property="og:description" content={t('faq.pageDescription')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://turbinago.com/faq" />
        <meta property="og:image" content="https://turbinago.com/og-default.png" />
        <meta property="og:site_name" content="Turbina" />
        <meta property="og:locale" content="es_419" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('faq.pageTitle')} />
        <meta name="twitter:description" content={t('faq.pageDescription')} />
        <meta name="twitter:image" content="https://turbinago.com/og-default.png" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: serializeJsonForScriptTag({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({
                '@type': 'Question',
                name: t(`faq.section${n}Question`),
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: t(`faq.section${n}Answer`),
                },
              })),
            }),
          }}
        />
      </Head>
      <Stack.Screen options={{ headerShown: false }} />
      <PublicHeader scrollOffset={scrollOffset} activeRoute="/faq" />

      <ScrollView
        className="flex-1"
        onScroll={(e) => setScrollOffset(e.nativeEvent.contentOffset.y)}
        scrollEventThrottle={16}
        contentContainerStyle={{
          flexGrow: 1,
        }}
      >
        <View className="flex-1 pt-6 pb-10 max-w-[1200px] self-center w-full">
          <View className="px-6">
            <View className="w-full">
              <View className="items-center gap-3 mb-8">
                <Text
                  className="text-xl font-medium native:font-medium text-center leading-tight"
                  style={{ color: colors.dark }}
                >
                  {t('faq.heading')}
                </Text>
                <Text
                  className="text-sm text-center leading-relaxed"
                  style={{ color: colors.muted }}
                >
                  {t('faq.intro')}
                </Text>
              </View>

              <View className="gap-6">
                {sections.map((section) => (
                  <View
                    key={section.title}
                    className="rounded-2xl p-6 gap-2"
                    style={{ backgroundColor: colors.surface }}
                  >
                    <Text
                      className="text-lg font-medium native:font-medium"
                      style={{ color: colors.dark }}
                    >
                      {section.title}
                    </Text>
                    <Text className="text-sm leading-relaxed" style={{ color: colors.muted }}>
                      {section.body}
                    </Text>
                  </View>
                ))}
              </View>

              <View
                className="rounded-2xl p-6 mt-8 items-center gap-2"
                style={{ backgroundColor: colors.surface }}
              >
                <Text
                  className="text-sm text-center leading-relaxed"
                  style={{ color: colors.muted }}
                >
                  {t('faq.contactPrompt')}
                </Text>
                <Pressable onPress={() => Linking.openURL(`mailto:${t('faq.contactEmail')}`)}>
                  <Text className="text-sm font-medium underline" style={{ color: colors.primary }}>
                    {t('faq.contactEmail')}
                  </Text>
                </Pressable>
              </View>
            </View>
          </View>
        </View>
        <Footer showTermsLink />
      </ScrollView>
    </View>
  )
}
